import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { getFocusableItems } from "@swa-ui/browser";
import { classNames, getUniqueId } from "@swa-ui/string";

import { AriaLive } from "../AriaLive";
import { keyCodes } from "../defines/keyCodes";
import { Flyout, flyoutPropTypes } from "../Flyout";
import { Icon } from "../Icon";
import { InformationTipLegacy } from "../InformationTipLegacy";
import { useCaption } from "../useCaption";
import { useCoreFeature } from "../useCoreFeature";
import styles from "./InformationTip.module.scss";

const DELAY_BEFORE_CLOSING = 200;
const DELAY_FOR_FOCUS = 16;
const INFORMATION_TIP_BG_COLOR_DESIGN_TOKEN = "cmp-core-color-information-tip-bg";
const INFORMATION_TIP_ICON_COLOR_DESIGN_TOKEN = "cmp-core-color-information-tip-icon";

/**
 * InformationTip provide a icon, which when clicked, displays an adjacent flyout (Caption) with
 * informational content.
 */

export const InformationTip = (props) => {
  const {
    className,
    content,
    initialLocation,
    location = "below",
    onClick,
    portal = false,
    size = "auto",
    width = "medium",
  } = props;

  const { captionRef, captionLocation, hideCaption, isCaptionVisible, showCaption } = useCaption(
    location,
    initialLocation,
    handleClose,
    false
  );
  const [hasFocusableChildren, setHasFocusableChildren] = useState(false);
  const contentId = useRef(`${getUniqueId("informationTip-content")}`);
  const informationTipId = useRef(`${getUniqueId("informationTip")}`);
  const isAdjoinPositionFallbackOn = useCoreFeature("adjoinPositionFallback");
  const isCssAnchorSupported = "anchorName" in document.documentElement.style;

  return isCssAnchorSupported && !isAdjoinPositionFallbackOn ? (
    <div data-unstable {...getProps()}>
      <AriaLive ariaLiveSetting="assertive" hiddenFromScreen={false}>
        <Flyout {...getFlyoutProps()}>
          <div {...getFocusContainerProps()}>
            <div className={styles.paddingContainer}>
              <Icon {...getIconProps()} />
            </div>
          </div>
        </Flyout>
      </AriaLive>
    </div>
  ) : (
    <InformationTipLegacy {...props} />
  );

  function getProps() {
    return {
      "aria-describedby": informationTipId.current,
      className: classNames(className, styles.informationTip, {
        [styles.focus]: captionLocation !== "hidden",
        [styles.small]: size === "small",
      }),
      onClick: handleClick,
      onKeyDown: handleKeyDown,
    };
  }

  function getFlyoutProps() {
    return {
      classNameAnchor: styles.classNameAnchor,
      classNameFlyout: styles.classNameFlyout,
      constrainFocus: hasFocusableChildren,
      flyout: <div {...getAdjoiningContentProps()}>{content}</div>,
      location: captionLocation,
      name: informationTipId.current,
      onTransformationEnd: handleTransformationEnd,
      portal,
      width,
    };
  }

  function getFocusContainerProps() {
    return {
      className: styles.focusContainer,
      onBlur: handleBlur,
      tabIndex: 0,
    };
  }

  function getAdjoiningContentProps() {
    return {
      "aria-label": props["aria-label"],
      className: styles.content,
      id: contentId.current,
      onClick: (event) => isTargetFocusableElement(event.target) && delayedClose(),
      ref: captionRef,
      role: "tooltip",
    };
  }

  function getIconProps() {
    return {
      background: {
        color: INFORMATION_TIP_BG_COLOR_DESIGN_TOKEN,
        type: "circle",
      },
      color: INFORMATION_TIP_ICON_COLOR_DESIGN_TOKEN,
      id: informationTipId.current,
      name: "Info",
      size: size === "auto" ? "size20" : "size12",
      transparentBorder: false,
    };
  }

  function handleClose(event) {
    event.stopPropagation();
  }

  function handleClick(event) {
    event.stopPropagation();
    isCaptionVisible ? hideCaption() : showCaption();
    onClick?.(isCaptionVisible);
  }

  function handleKeyDown(event) {
    const { key } = event;
    const focusableElements = getFocusableElements();

    if (captionLocation === "hidden") {
      if (key === keyCodes.KEY_ENTER || key === keyCodes.KEY_SPACE) {
        event.preventDefault();
        event.stopPropagation();
        showCaption();
      }
    } else {
      if (key === keyCodes.KEY_ESCAPE || (key === keyCodes.KEY_TAB && !focusableElements.length)) {
        hideCaption();
      } else if (key === keyCodes.KEY_ENTER) {
        delayedClose();
      } else if (key === keyCodes.KEY_SPACE) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  function handleBlur() {
    !hasFocusableChildren && hideCaption();
  }

  function handleTransformationEnd() {
    setHasFocusableChildren(isFocusableItemsInInfoTip());
  }

  function isFocusableItemsInInfoTip() {
    const focusableElements = getFocusableElements();

    return focusableElements?.length > 0;
  }

  function isTargetFocusableElement(target) {
    const focusableElements = getFocusableElements();

    return focusableElements.find((item) => item === target || item.contains(target));
  }

  function getFocusableElements() {
    return getFocusableItems(`[id="${contentId.current}"]`);
  }

  function delayedClose() {
    setTimeout(() => hideCaption(), DELAY_BEFORE_CLOSING);
  }
};

InformationTip.displayName = "InformationTip";

export const informationTipPropTypes = {
  /** accessibility label applied to the Icon used. */
  "aria-label": PropTypes.string,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Content that will appear in the flyout of Caption. */
  content: PropTypes.node,

  /** Initial location to display flyout. */
  initialLocation: PropTypes.oneOf(["above", "below", "hidden", "left", "right"]),

  /** Location to display flyout once it's opened. */
  location: PropTypes.oneOf(["above", "below", "hidden", "left", "right"]),

  /** Callback function to learn when a informationTip is clicked. The only parameter returned is isCaptionVisible. */
  onClick: PropTypes.func,

  /** Whether to render the flyout content in a React portal. */
  portal: PropTypes.bool,

  /** Size of information icon. */
  size: PropTypes.oneOf(["auto", "small"]),

  /**
   * Width for InformationTip content. If a width is not given, the width will be governed by content.
   */
  width: flyoutPropTypes.width,
};

InformationTip.propTypes = informationTipPropTypes;
InformationTip.displayName = "InformationTip";
